import { useEffect, useRef } from 'react'
import Hls from 'hls.js'
import * as Sentry from '@sentry/node'

type Props = {
  src: string
  poster?: string
  autoplay?: boolean
  isVideoSourceMux?: boolean
}

const MUX_VIDEO_CODEC = 'application/vnd.apple.mpegurl'
const VALID_CODECS = ['video/mp4', 'video/webm', MUX_VIDEO_CODEC]

/** Supports mp4, webm and MUX videos. For MUX videos, add `isVideoSourceMux`. */
const VideoPlayer = ({
  src,
  poster,
  autoplay = false,
  isVideoSourceMux = false,
}: Props) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    video.controls = true

    const canPlayType = isVideoSourceMux
      ? video.canPlayType(MUX_VIDEO_CODEC)
      : VALID_CODECS.some((codec) => video.canPlayType(codec))

    if (canPlayType) {
      video.src = src
      video.autoplay = autoplay
    } else if (Hls.isSupported()) {
      /**
       * HLS.js is a JavaScript library that implements an HTTP Live Streaming client.
       * It relies on HTML5 video and MediaSource Extensions for playback.
       *
       * It works by transmuxing MPEG-2 Transport Stream and AAC/MP3 streams into ISO BMFF (MP4) fragments.
       * Transmuxing is performed asynchronously using a Web Worker when available in the browser.
       * HLS.js also supports HLS + fmp4, as announced during WWDC2016.
       *
       * HLS.js works directly on top of a standard HTML<video> element.
       * https://github.com/video-dev/hls.js
       */
      const hls = new Hls()
      hls.loadSource(src)
      hls.attachMedia(video)
    } else {
      Sentry.captureMessage(
        'This is an old browser that does not support MSE https://developer.mozilla.org/en-US/docs/Web/API/Media_Source_Extensions_API'
      )
    }

    video.onplay = blurActiveElement
  }, [src, videoRef])

  const blurActiveElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }

  return (
    <video
      className="max-w-full max-h-full rounded-lg focus:outline-none"
      ref={videoRef}
      poster={poster}
      onTouchEnd={blurActiveElement}
    />
  )
}

export default VideoPlayer
